import { useState, useEffect, useCallback, useRef } from "react";
import { useSocketContext } from "../context/SocketProvider";
import { config } from "../Environment";
import useRetryFetch from "./useRetryFetch";

export interface Notification {
  _id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  metadata: {
    processId?: string;
    chatId?: string;
    fromUser?: {
      _id: string;
      email: string;
      displayName: string;
    };
    prescriptionId?: string;
    actionUrl?: string;
    actionType?: string;
    customData?: any;
  };
  isRead: boolean;
  createdAt: number;
  readAt?: number;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
  hasMore: boolean;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const { socket } = useSocketContext();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const retryFetch = useRetryFetch();

  // Load sound preference from localStorage
  useEffect(() => {
    const savedPreference = localStorage.getItem("notificationSoundEnabled");
    if (savedPreference !== null) {
      setSoundEnabled(JSON.parse(savedPreference));
    }
  }, []);

  // Save sound preference to localStorage
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem("notificationSoundEnabled", JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.error("Failed to play notification sound:", error);
      });
    }
  }, [soundEnabled]);

  // Fetch notifications from API
  const fetchNotifications = useCallback(
    async (unreadOnly = false, skip = 0, limit = 50) => {
      setIsLoading(true);
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/notifications?unreadOnly=${unreadOnly}&skip=${skip}&limit=${limit}`,
          {
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }

        const data: NotificationsResponse = await response.json();

        if (skip === 0) {
          setNotifications(data.notifications);
        } else {
          setNotifications((prev) => [...prev, ...data.notifications]);
        }

        setUnreadCount(data.unreadCount);
        setHasMore(data.hasMore);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/notifications/unread-count`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch unread count");
      }

      const data = await response.json();
      setUnreadCount(data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  // Mark notification as read (optimistic)
  const markAsRead = useCallback(async (notificationId: string) => {
    // Optimistic update
    const prevNotifications = [...notifications];
    const prevUnread = unreadCount;
    const wasUnread = notifications.find((n) => n._id === notificationId && !n.isRead);

    setNotifications((prev) =>
      prev.map((n) =>
        n._id === notificationId ? { ...n, isRead: true, readAt: Date.now() } : n
      )
    );
    if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/notifications/${notificationId}/read`,
        {
          method: "PUT",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to mark notification as read");
      }
    } catch (error) {
      // Rollback
      setNotifications(prevNotifications);
      setUnreadCount(prevUnread);
      console.error("Error marking notification as read:", error);
    }
  }, [notifications, unreadCount]);

  // Mark all notifications as read (optimistic)
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    const prevNotifications = [...notifications];
    const prevUnread = unreadCount;

    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, readAt: Date.now() }))
    );
    setUnreadCount(0);

    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/notifications/read-all`,
        {
          method: "PUT",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to mark all notifications as read");
      }
    } catch (error) {
      // Rollback
      setNotifications(prevNotifications);
      setUnreadCount(prevUnread);
      console.error("Error marking all notifications as read:", error);
    }
  }, [notifications, unreadCount]);

  // Delete notification (optimistic)
  const deleteNotification = useCallback(async (notificationId: string) => {
    // Optimistic update
    const prevNotifications = [...notifications];
    const prevUnread = unreadCount;
    const notification = notifications.find((n) => n._id === notificationId);

    setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    if (notification && !notification.isRead) {
      setUnreadCount((count) => Math.max(0, count - 1));
    }

    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/notifications/${notificationId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete notification");
      }
    } catch (error) {
      // Rollback
      setNotifications(prevNotifications);
      setUnreadCount(prevUnread);
      console.error("Error deleting notification:", error);
    }
  }, [notifications, unreadCount]);

  // Delete all read notifications
  const deleteAllRead = useCallback(async () => {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/notifications/read-all`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete read notifications");
      }

      // Update local state
      setNotifications((prev) => prev.filter((n) => !n.isRead));
    } catch (error) {
      console.error("Error deleting read notifications:", error);
    }
  }, []);

  // Handle real-time notification events
  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notification: Notification) => {
      console.log("New notification received:", notification);

      // Add to notifications list
      setNotifications((prev) => [notification, ...prev]);

      // Increment unread count
      setUnreadCount((prev) => prev + 1);

      // Play notification sound
      playNotificationSound();
    };

    const handleUnreadCount = (data: { count: number }) => {
      console.log("Unread count update:", data.count);
      setUnreadCount(data.count);
    };

    socket.on("notification:new", handleNewNotification);
    socket.on("notification:unread-count", handleUnreadCount);

    return () => {
      socket.off("notification:new", handleNewNotification);
      socket.off("notification:unread-count", handleUnreadCount);
    };
  }, [socket, playNotificationSound]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    soundEnabled,
    toggleSound,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    audioRef,
  };
}
